import React, {useMemo} from 'react'
import {
  EditButton,
  FilesListDisplay,
  getPriorityVariant,
  RPCDocs,
  UserAnPCheck,
} from '@therms/react-modules'
import {humanizeMinutes} from '@therms/web-js'
import dayjs from 'dayjs'
import classNames from 'classnames'
import {Image, Modal} from '@therms/atalaya'

interface BoloModalProps {
  bolo:
    | RPCDocs.Locations.GetLocationBolosList.v1.Bolo
    | RPCDocs.Bolos.GetBolo.v1.Bolo
  closeHandler?: () => void
  onEditClick?: (boloId: string) => void
}

function BoloModal({bolo, closeHandler, onEditClick}: BoloModalProps) {
  const {
    active,
    activeDate,
    body,
    expireDate,
    id,
    primaryImageUrl,
    priority,
    title,
  } = bolo

  const statusMessage = useMemo(() => {
    let status
    let message
    if (active) {
      status = 'active'
      message = `Expires in ${humanizeMinutes(
        dayjs().diff(dayjs(expireDate), 'minute'),
      )}`
    } else if (dayjs().isBefore(dayjs(activeDate))) {
      status = 'future'
      message = `Activates in ${humanizeMinutes(
        dayjs().diff(dayjs(activeDate), 'minute'),
      )}`
    } else if (dayjs().isAfter(dayjs(expireDate))) {
      status = 'expired'
      message = `Expired ${humanizeMinutes(
        dayjs(expireDate).diff(dayjs(), 'minute'),
      )} ago`
    }

    return (
      <span
        className={
          status !== 'active' ? 'text-color font-medium' : 'text-color-subtle'
        }
      >
        {message}
      </span>
    )
  }, [active, activeDate, expireDate])

  const priorityVariant = getPriorityVariant(priority)

  return (
    <Modal
      closeHandler={closeHandler}
      escPressClose
      header={
        <UserAnPCheck
          check={(user) => user.anp.role.ops.permissions.createBolos}
        >
          <EditButton onClick={() => onEditClick?.(id)} />
        </UserAnPCheck>
      }
      width="450px"
    >
      {!!primaryImageUrl && (
        <Image src={primaryImageUrl} fluid fit="cover" rounded height={300} />
      )}

      <div className={primaryImageUrl && 'mt-sm'}>{statusMessage}</div>

      <div className="text-lg font-medium capitalize">{title}</div>

      <div
        className={classNames(
          {
            'border-border': !priorityVariant,
            'border-caution': priorityVariant === 'caution',
            'border-critical': priorityVariant === 'critical',
            'border-info': priorityVariant === 'info',
          },
          'mt-sm border-t',
        )}
      />

      <div className="mt-sm whitespace-pre-wrap">{body}</div>

      {Array.isArray(bolo.fileUrls) && !!bolo.fileUrls.length && (
        <div className="mt-sm">
          <FilesListDisplay fileUrls={bolo.fileUrls} />
        </div>
      )}
    </Modal>
  )
}

export {BoloModal}
